import { css, StyleSheet } from 'aphrodite';
import { type FormEvent, useState } from 'react';
import { useCreateFileRequestMutation } from '../../../../../../../queries';
import { Button } from '../../../../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../../components-v2/Dialog';
import { Field, FieldDescription, FieldError, FieldLabel } from '../../../../../../components-v2/Field';
import { Input } from '../../../../../../components-v2/Input';
import { TextArea } from '../../../../../../components-v2/TextArea';
import { SPACE } from '../../../../../../components-v2/Theme/theme';
import { userFacingErrorMessage } from '../userFacingErrorMessage';

export const MAX_DESCRIPTION_LENGTH = 1000;

const styleSheet = StyleSheet.create({
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
	},
});

interface IProps {
	contactId: string;
	onClose: () => void;
	onCreated: (link: string) => void;
	open: boolean;
}

export const RequestFilesDialog = ({ contactId, onClose, onCreated, open }: IProps) => {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [showNameError, setShowNameError] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const createFileRequest = useCreateFileRequestMutation();

	const reset = () => {
		setName('');
		setDescription('');
		setShowNameError(false);
		setSubmitError(null);
	};

	const close = () => {
		if (createFileRequest.isPending) {
			return;
		}
		reset();
		onClose();
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (!name.trim()) {
			setShowNameError(true);
			return;
		}
		setSubmitError(null);
		createFileRequest.mutate(
			{ contactId, description, name },
			{
				onError: error =>
					setSubmitError(userFacingErrorMessage(error, 'Could not create the file request. Try again.')),
				onSuccess: link => {
					if (!link) {
						setSubmitError('Could not create the file request. Try again.');
						return;
					}
					reset();
					onCreated(link);
				},
			}
		);
	};

	return (
		<Dialog open={open} onOpenChange={next => !next && close()}>
			<DialogPopup>
				<form onSubmit={handleSubmit} noValidate>
					<DialogHeader>
						<DialogTitle>Request files</DialogTitle>
						<DialogDescription>
							Tell this contact what you need. They’ll see it on the secure upload page.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.form)}>
							<Field>
								<FieldLabel htmlFor='file-request-name'>What are you requesting?</FieldLabel>
								<Input
									aria-invalid={(showNameError && !name.trim()) || undefined}
									autoFocus
									id='file-request-name'
									onChange={e => {
										setName(e.target.value);
										setShowNameError(false);
									}}
									placeholder='e.g. 2025 W-2'
									value={name}
								/>
								{showNameError && !name.trim() ? <FieldError>Add a title for this request.</FieldError> : null}
							</Field>
							<Field>
								<FieldLabel htmlFor='file-request-description'>Details (optional)</FieldLabel>
								<TextArea
									id='file-request-description'
									maxLength={MAX_DESCRIPTION_LENGTH}
									onChange={e => setDescription(e.target.value)}
									placeholder='Anything that helps them send the right documents'
									rows={4}
									value={description}
								/>
								<FieldDescription>
									{description.length}/{MAX_DESCRIPTION_LENGTH}
								</FieldDescription>
							</Field>
							{submitError ? <FieldError>{submitError}</FieldError> : null}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button disabled={createFileRequest.isPending} onClick={close} type='button' variant='secondary'>
							Cancel
						</Button>
						<Button isLoading={createFileRequest.isPending} type='submit'>
							Continue to email
						</Button>
					</DialogFooter>
				</form>
			</DialogPopup>
		</Dialog>
	);
};
